import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { IgniteDropdown } from '@kar-dmp/pattern-library/react';
import {
  DropdownVariant,
  FeedbackStatus,
  Icon
} from '@kar-dmp/pattern-library';
import { useOmpDealerContext } from '../OmpDealerProvider';
import Alert from '../../../_shared/components/Alert';
import Loading from '../../../_shared/components/Loading';
import { DealershipSelectionContainer } from './Styles';
import TRToast from '../../../_shared/helpers/TRToast';
import { BUSINESS_UNIT } from '../../../_shared/constants/omp';

const DealerSelectDropdown = ({ biddingDealerId, hasError, isLoading }) => {
  const { formatMessage } = useIntl();
  const {
    ompBiddingDealer,
    setOmpBiddingDealer,
    validOmpDealerships
  } = useOmpDealerContext();
  const [dealershipOptions, setDealershipOptions] = useState(
    validOmpDealerships
      ?.map(dealership => {
        const labelWithDefault = `${dealership.displayName} ${formatMessage({
          id: 'vdp.bid.default'
        })}`;

        return {
          label: dealership.isDefault
            ? labelWithDefault
            : dealership.displayName,
          value: dealership.organizationId
        };
      })
      .filter(option => option.label && option.value)
  );

  const handleChange = useCallback(
    e => {
      const newBiddingDealer = validOmpDealerships.find(
        dealerShip => dealerShip.organizationId === e?.detail
      );
      setOmpBiddingDealer(newBiddingDealer);

      TRToast(
        <Alert
          variant="information"
          messages={[<FormattedMessage id="vdp.bid.switchDealerships" />]}
        />
      );
    },
    [setOmpBiddingDealer, validOmpDealerships]
  );

  useEffect(() => {
    if (biddingDealerId) {
      const newBiddingDealer = validOmpDealerships?.find(dealership =>
        dealership.sourceInfo.find(
          source =>
            source.businessUnit === BUSINESS_UNIT.TRADEREV &&
            parseInt(source.sourceId, 10) === parseInt(biddingDealerId, 10)
        )
      );
      setOmpBiddingDealer(newBiddingDealer);
    }
  }, [biddingDealerId, setOmpBiddingDealer, validOmpDealerships]);

  useEffect(() => {
    // Transform the dealership options into the valid shape for KarDropdown
    if (validOmpDealerships?.length) {
      const options = validOmpDealerships?.map(dealership => {
        const labelWithDefault = `${dealership.displayName} ${formatMessage({
          id: 'vdp.bid.default'
        })}`;

        return {
          label: dealership.isDefault
            ? labelWithDefault
            : dealership.displayName,
          value: dealership.organizationId
        };
      });

      setDealershipOptions(
        options?.filter(option => option.label && option.value)
      );
    }
  }, [validOmpDealerships, formatMessage]);

  if (isLoading) {
    return (
      <DealershipSelectionContainer>
        <Loading width="100%" height="48px" />
      </DealershipSelectionContainer>
    );
  }

  return (
    <DealershipSelectionContainer data-testid="vdp-dealer-select__container">
      <IgniteDropdown
        data-testid="vdp-dealer-select__dropdown"
        disabled={
          !!biddingDealerId ||
          (dealershipOptions?.length === 1 &&
            ompBiddingDealer?.organizationId &&
            dealershipOptions.includes(
              option =>
                option.organizationId !== ompBiddingDealer.organizationId
            ))
        }
        icon={Icon.Dealership}
        onDropdownChange={handleChange}
        options={dealershipOptions}
        placeholder={formatMessage({
          id: 'tr5.select'
        })}
        prefixText={formatMessage({
          id: 'vdp.bid.selectDealership.prefix'
        })}
        required
        status={hasError ? FeedbackStatus.Error : FeedbackStatus.Neutral}
        hintText={
          hasError
            ? formatMessage({
                id: 'vdp.bid.selectDealership.invalidDealership'
              })
            : undefined
        }
        value={ompBiddingDealer?.organizationId}
        variant={DropdownVariant.Filled}
      />
    </DealershipSelectionContainer>
  );
};

export default DealerSelectDropdown;
